import { get as getCookie } from 'js-cookie';
import React, { useEffect, useState } from 'react';
import { Control, UseFormReset, useForm } from 'react-hook-form';
import useSWR from 'swr';

import { useLoggedInEmployeeProfile } from '~/components/Auth';
import { LEGACY_COOKIE } from '~/data/cookies/';
import { useEmployee } from '~/data/employee';
import { useAllEnums } from '~/data/enums';
import {
  getCountryByLegalEntity,
  useAllLegalEntities,
} from '~/data/legalEntity';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import {
  EditCustomField,
  ReadCustomField,
  filterEnumsByCountry,
  jsEditCustomFieldData,
  readCustomData,
} from '~/legacy-components/CustomDataFields';
import { getCurrentLanguage } from '~/localization';

import { AnswerPreProcess } from '../../../types';

import {
  getCustomEmployeePositionFieldAttributes,
  getCustomEmployeePositionFieldLanguages,
  getCustomEmployeePositionFields,
  saveCustomFieldValues,
} from './data';

export type UseCustomPositionOutput = {
  isLoading: boolean;
  error: string;
  canEdit: boolean;
  readFieldData: ReadCustomField[];
  editFieldData: EditCustomField[];
  handleFormSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  control: Control<AnswerPreProcess>;
  reset: UseFormReset<AnswerPreProcess>;
  isSubmitSuccessful: boolean;
  submitCount: number;
};

export const useCustomPosition = (empId: string): UseCustomPositionOutput => {
  const userProfile = useLoggedInEmployeeProfile();
  const [error, setError] = useState<string>('');
  const [readFieldData, setReadFieldData] = useState<ReadCustomField[]>([]);
  const [editFieldData, setEditFieldData] = useState<EditCustomField[]>([]);
  const iRehire = getCookie(LEGACY_COOKIE.REHIRE) === 'active' ? true : false;
  const currentLanguage = getCurrentLanguage();

  const { data: customEmployeeFieldsRes, isLoading: employeeFieldsLoading } =
    useSWR(['customEmployeePositionFields', empId], () =>
      getCustomEmployeePositionFields(empId),
    );
  const {
    data: customEmployeeFieldLanguagesRes,
    isLoading: employeeFieldLanguageLoading,
  } = useSWR(['customEmployeePositionFieldLanguages', currentLanguage], () =>
    getCustomEmployeePositionFieldLanguages(currentLanguage),
  );
  const {
    data: customEmployeeFieldAttributesRes,
    isLoading: fieldAttributesLoading,
  } = useSWR('customEmployeePositionFieldsFieldAttributesPosition', () =>
    getCustomEmployeePositionFieldAttributes(),
  );
  const {
    legalEntities,
    isLoading: legalEntitiesLoading,
    hasData: legalEntityHasData,
  } = useAllLegalEntities();
  const { allEnums } = useAllEnums();
  const isLoading =
    fieldAttributesLoading ||
    employeeFieldsLoading ||
    employeeFieldLanguageLoading ||
    (legalEntitiesLoading && !legalEntityHasData);

  const {
    isSelectedEmployeeLoggedInUser,
    selectedEmployeeDetails: { hasRoleAccessOverEmployee, isMyselfWithRole },
  } = useSelectedEmployeeId();

  const allowedRoles: Roles[] = [
    Roles.HR_STAFF,
    Roles.HR_SALARY,
    Roles.HR_DEV,
    Roles.HR_ADMIN,
    Roles.MANAGER,
    Roles.ALT_MANAGER,
    Roles.DEPUTY_MANAGER,
  ];
  const canEdit: boolean =
    (!isSelectedEmployeeLoggedInUser &&
      hasRoleAccessOverEmployee(allowedRoles)) ||
    isMyselfWithRole([Roles.HR_ADMIN]);

  const {
    handleSubmit,
    formState: { isSubmitSuccessful, submitCount },
    control,
    reset,
  } = useForm<AnswerPreProcess>();
  const handleFormSubmit = React.useMemo(() => {
    return handleSubmit(async (data) => {
      await saveCustomFieldValues(empId, data);
    });
  }, [empId, handleSubmit]);

  const {
    employee: { empBasicData, empPosition, empPositionPending },
  } = useEmployee();

  const legalEntityId =
    ['NEW', 'PENDING'].includes(empBasicData.fEmpStatus) && empPositionPending
      ? empPositionPending.fEmpLegalEntity
      : empPosition.fEmpLegalEntity;

  useEffect(() => {
    try {
      if (!isLoading) {
        const legalEntityCountry = getCountryByLegalEntity(
          userProfile.legalEntity.id,
          legalEntities,
        );
        const empEnums = filterEnumsByCountry(
          customEmployeeFieldLanguagesRes!,
          '',
          legalEntityCountry,
        );
        const hashedFields = Object.assign(
          {},
          ...customEmployeeFieldAttributesRes!.map((field) => ({
            [field.fCustomDataAttributeName]: field,
          })),
        );
        const legalEntityCountryForUser = getCountryByLegalEntity(
          legalEntityId,
          legalEntities,
        );
        if (!iRehire && empEnums.length > 0) {
          setReadFieldData(
            readCustomData(
              'EMPLOYEE_POS',
              hashedFields,
              customEmployeeFieldsRes!,
              legalEntityCountryForUser,
              allEnums,
            ),
          );
          setEditFieldData(
            jsEditCustomFieldData({
              strEntity: 'EMPLOYEE_POS',
              customFieldAttributesHash: hashedFields,
              customFields: customEmployeeFieldsRes!,
              legalEntityCountry: legalEntityCountryForUser,
              allEnums,
            }),
          );
        }
      }
    } catch (err: unknown) {
      setError(`${err}`);
    }
  }, [
    allEnums,
    customEmployeeFieldAttributesRes,
    customEmployeeFieldLanguagesRes,
    customEmployeeFieldsRes,
    iRehire,
    isLoading,
    legalEntities,
    legalEntityId,
    userProfile.legalEntity.id,
  ]);

  return {
    reset,
    isLoading,
    error,
    canEdit,
    readFieldData,
    editFieldData,
    handleFormSubmit,
    control,
    isSubmitSuccessful,
    submitCount,
  };
};
